import invariant from 'invariant';
import { isUUID } from '@frameio/utils/formatters';
// @todo (WEB-1045): Add condensed UUID support to routes
// import { toCondensedUUID } from '@frameio/utils/formatters/uuid';
import { createUrl } from '../helpers/createUrl';
import { getPathnameFromHref } from '../helpers/getPathnameFromHref';
import type { ValidHref } from '../types';

// @todo (WEB-1045): Remove when condensed UUID support is added to routes
const toCondensedUUID = (uuid: string): string => uuid;

export type FolderGetLinkParams =
  /**
   * FolderPage
   * The root folder of a project
   * /project/:projectId
   */
  | {
      page: 'folder';
      projectId: string;
    }
  /**
   * FolderPage
   * A subfolder of a project
   * /project/:projectId/:folderAssetId
   */
  | {
      page: 'folder';
      projectId: string;
      folderAssetId: string;
      assetId?: string;
    };

export const getLinkForFolder = (params: FolderGetLinkParams): ValidHref => {
  // Root folder
  const { projectId } = params;
  invariant(projectId, 'projectId is required');
  const _projectId = toCondensedUUID(projectId);

  // /project/:projectId
  let newUrl = createUrl({
    pathname: `/project/${_projectId}`,
  });
  const urlSearchParams = new URLSearchParams();

  // ... ?assetId=:assetId
  if ('assetId' in params && params.assetId) {
    urlSearchParams.set('assetId', toCondensedUUID(params.assetId));
  }

  // Subfolder
  if ('folderAssetId' in params && params.folderAssetId) {
    const { folderAssetId } = params;
    invariant(projectId, 'projectId is required');

    const _folderAssetId = toCondensedUUID(folderAssetId);

    newUrl = createUrl({
      pathname: `/project/${_projectId}/${_folderAssetId}`,
    });
  }

  if (urlSearchParams.toString()) {
    newUrl += `?${
      urlSearchParams.toString() as {
        a?: string;
      }
    }`;
  }

  return newUrl as ValidHref;
};

export const isFolderPageType = (href: ValidHref): boolean => {
  const pathname = getPathnameFromHref(href);
  const parts = pathname.split('/');
  if (parts[1] !== 'project') return false;

  // /project/:projectId
  if (parts.length === 3 && isUUID(parts[2])) return true;

  // /project/:projectId/:folderAssetId;
  // Note: need to make sure this doesn't match routes like /project/:projectId/shares
  if (parts.length === 4 && isUUID(parts[2]) && isUUID(parts[3])) return true;

  return false;
};
